import React from 'react';
import { Helmet } from 'react-helmet';
import Styled from 'styled-components';
import { Header, Icon, Confirm, Modal, Pagination } from 'semantic-ui-react';
import moment from 'moment';
import Table from 'semantic-table-grid';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Components
import FilterBar from 'components/TutorList/FilterBar';
import { BtnWithActions } from 'components/Buttons';
import TutorResumesCards from 'components/TutorList/TutorResumesCards';

// Services
import aclService from 'shared/lib/acl';
import ReteTutorService from 'shared/services/api/ReteTutor';
import { ACL, ROUTES } from 'shared/constants';
import TutorUpdateState from '../TutorUpdateState';
import { RETE_TUTOR_STATE } from './constantReteTutor';

const TableOverflow = Styled.div`
  display: block;
  margin: -14px -14px -10px;
  overflow-x: auto;
  padding: 14px;
  & td {
    position: relative;
  }
`;

const TitlteHeader = Styled(Header)`
  margin-top: 15px;
`;

class TutorList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false, // Manage loading of table
      page: 1, // Manage current page for pagination of table of table
      totalPages: 1, // Manage total pages for pagination of table
      tutor: [], // Recessi ti view
      lastFilters: {}, // Manage filter
      orderField: 'dataRichiesta', // Manage order params for table
      orderDirection: 'asc', // Manage order params for table
      currentTutor: null, // Current data of recesso
      removeTutorConfirm: false, // Modal to remove recesso
      updateState: false, // Modal to update state
      totalRecessi: null, // Total of recessi
    };
  }

  componentDidMount() {
    this.loadData();
  }

  /** Define action for each tutor */
  getActions = (tutor) =>
    [
      {
        icon: 'edit',
        label: 'Modifica',
        action: () =>
          _.get(tutor, 'tipoRichiesta') === 'Nuovo tutor'
            ? this.props.history.push(`${ROUTES.ADD_RETE_TUTOR}/${tutor.id}`)
            : this.props.history.push(`${ROUTES.UPDATE_RETE_TUTOR}/${tutor.id}`),
      },
      (aclService.check(this.props.auth, ACL.CAN_UPDATE_RETE_TUTOR) ||
        (aclService.check(this.props.auth, ['responsabileArea']) && tutor.state <= 1)) && {
          icon: 'exchange',
          label: 'Aggiorna stato',
          action: () => this.setState({ updateState: true, currentTutor: tutor }),
        },
    ].filter(Boolean);

  /** Check direction of menu */
  isMenuUpward = (tutor) => this.state.tutor.length - _.findLastIndex(this.state.tutor, tutor) < 3;

  /** Define columns for table */
  columns = [
    {
      key: 'tipoRichiesta',
      name: 'Tipo',
      formatter: ({ data }) => <div style={{ whiteSpace: 'nowrap' }}>{data.tipoRichiesta}</div>,
      sortable: false,
    },
    {
      key: 'tutorProfile',
      name: 'Tutor / Profilo',
      formatter: ({ data }) => (
        <div>
          <span style={{ whiteSpace: 'nowrap' }}>
            {_.get(data, 'nominative') || null}
            <br></br>
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            {_.get(data, 'tutor.tipoProfilo') || null}
            <br></br>
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      key: 'seniorManager',
      name: 'Senior Manager / Agenzia',
      formatter: ({ data }) => (
        <div>
          <span style={{ whiteSpace: 'nowrap' }}>
            {_.get(data, 'tutor.seniorManager') || null}
            <br></br>
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            {_.get(data, 'tutor.agenzia') || null}
            <br></br>
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      key: 'areaManager',
      name: "Responsabile d'area",
      formatter: ({ data }) => (
        <div style={{ whiteSpace: 'nowrap' }}>{_.get(data, 'tutor.responsabileArea') || null}</div>
      ),
      sortable: false,
    },
    {
      key: 'tutee',
      name: 'Tutee',
      formatter: ({ data }) => {
        if (!_.has(data, '_embedded.tutee')) {
          return null;
        }

        return data._embedded.tutee.map((tutee) => (
          <span key={tutee.id} style={{ whiteSpace: 'nowrap', marginBottom: '4px' }}>
            {_.get(tutee, 'collaboratore.nominativo') || null} ({_.get(tutee, 'percentuale')}%)
            <br />
          </span>
        ));
      },
      sortable: false,
    },
    {
      key: 'stato',
      name: 'Stato',
      formatter: ({ data }) => RETE_TUTOR_STATE[data.state],
      sortable: false,
    },
    {
      key: 'dataRichiesta',
      name: 'Data richiesta',
      formatter: ({ data }) => (
        <div>{_.get(data, 'dataRichiesta') ? moment.unix(data.dataRichiesta).format('DD/MM/YYYY') : null}</div>
      ),
      sortable: true,
    },
    {
      key: 'alert',
      name: 'Alert',
      formatter: ({ data }) => <div style={{ whiteSpace: 'nowrap' }}>{data.alert}</div>,
      sortable: false,
    },
    {
      key: 'action',
      name: 'Azioni',
      formatter: ({ data }) => (
        <div style={{ textAlign: 'center' }}>
          <BtnWithActions
            key="other-actions"
            actions={this.getActions(data)}
            otherActions={[]}
            upward={this.isMenuUpward(data)}
          />
        </div>
      ),
      width: 'auto',
    },
  ];

  /** Load all tutor */
  loadData = (filters, isFiltering) => {
    this.setState({ isLoading: true });

    let currentPage = this.state.page;

    if (isFiltering !== false) {
      currentPage = 1;
      this.setState({ page: 1 });
    }

    if (!_.isEmpty(filters)) {
      this.setState({ lastFilters: filters });
    }

    if (currentPage === 0) {
      currentPage = 1;
    }

    ReteTutorService.getAll(currentPage, filters || { active: true }, this.state.orderField, this.state.orderDirection)
      .then((res) => {
        const reteTutor = res._embedded.rete_tutor || [];
        const tutorNullable = reteTutor.filter((item) => item.dataRichiesta === null);
        _.remove(reteTutor, (item) => item.dataRichiesta === null);

        this.setState({ totalPages: res.page_count, tutor: [...reteTutor, ...tutorNullable] });
      })
      .catch((err) =>
        this.props.notification().addNotification({
          message: _.get(err, 'data.detail') || 'Errore nel recupero dei dati',
          level: 'error',
        })
      )
      .finally(() => this.setState({ isLoading: false }));
  };

  /** Call api to remove tutor */
  removeTutor = () => {
    ReteTutorService.deleteTutor(this.state.currentTutor.id)
      .then(() => {
        this.props.notification().addNotification({
          message: 'Eliminazione avvenuta con successo',
          level: 'success',
        });
        this.closeModalRemoveTutor();
        this.loadData();
      })
      .catch((err) =>
        this.props.notification().addNotification({
          message:
            _.get(err, 'data.detail') || "Errore durante l'eliminazione, se il problema persiste contatta l'assistenza",
          level: 'error',
        })
      );
  };

  /** Redirect to form to add new tutor */
  addRecessoTutor = () => this.props.history.push(`${ROUTES.ADD_RETE_TUTOR}/new`);

  /** Redirect to form to update new tutor */
  updateReteTutor = () => this.props.history.push(`${ROUTES.UPDATE_RETE_TUTOR}/new`);

  /** Close modal to remove tutor */
  closeModalRemoveTutor = () => this.setState({ currentTutor: null, removeTutorConfirm: false });

  /** Close modal to update state */
  closeModalUpdateState = () => this.setState({ currentTutor: null, updateState: false });

  render() {
    const { auth } = this.props;

    return (
      <div>
        <Helmet>
          <title>Tutor</title>
          <meta name="description" content="Tutor" />
        </Helmet>

        <TitlteHeader as="h2">
          <Icon name="users" />
          <Header.Content>
            Tutor
            <Header.Subheader>Creazione e modifica tutor</Header.Subheader>
          </Header.Content>
        </TitlteHeader>

        <TutorResumesCards />

        <TableOverflow>
          <Table
            isLoading={this.state.isLoading}
            columns={this.columns}
            canAction={{
              actions: (
                <FilterBar
                  auth={auth}
                  addRecessoTutor={this.addRecessoTutor}
                  onFilter={this.loadData}
                  updateReteTutor={this.updateReteTutor}
                />
              ),
              active: true,
            }}
            elements={this.state.tutor}
            canSort={{
              active: true,
              onSort: (field, dir) => {
                this.setState({ orderField: field, orderDirection: dir }, () => this.loadData(this.state.lastFilters));
              },
            }}
            canPaginate={{
              active: true,
              render: (
                <Pagination
                  activePage={this.state.page}
                  boundaryRange={1}
                  onPageChange={(_e, { activePage }) =>
                    this.setState({ page: activePage }, () => this.loadData(this.state.lastFilters, false))
                  }
                  size="mini"
                  siblingRange={1}
                  totalPages={this.state.totalPages}
                  ellipsisItem={'...'}
                  lastItem={null}
                />
              ),
            }}
          />
        </TableOverflow>

        <Modal size="mini" closeIcon open={this.state.updateState} onClose={this.closeModalUpdateState}>
          <Header content="Aggiorna stato" />
          <TutorUpdateState
            closeModal={this.closeModalUpdateState}
            tutor={this.state.currentTutor}
            notification={this.props.notification}
            loadData={this.loadData}
            auth={auth}
          />
        </Modal>

        <Confirm
          size="tiny"
          content="Sei sicuro di voler eliminare il recesso?"
          open={this.state.removeTutorConfirm}
          cancelButton="Annulla"
          confirmButton="Conferma"
          onCancel={this.closeModalRemoveTutor}
          onConfirm={this.removeTutor}
        />
      </div>
    );
  }
}

TutorList.propTypes = {
  /** Used for manage history of the browser -> redirect */
  history: PropTypes.object,
  /** Function for call the notification system */
  notification: PropTypes.func,
  /** Object with data of logged user */
  auth: PropTypes.object,
};

export default TutorList;



// WEBPACK FOOTER //
// ./app/components/TutorList/List/index.js